import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Venue } from '@guabi/shared';
import { Button } from '@/components/ui/Button';
import { apiGet, ApiRequestError } from '@/lib/api';
import {
  initialAutosave,
  keepMine,
  onConflict,
  onError,
  onRemote,
  onSaveStart,
  onSaved,
  onType,
  shouldSave,
  takeTheirs,
  type AutosaveState,
} from './notesAutosave';
import { usePatchVenueField } from './queries';

/**
 * The notes, always open and saving themselves.
 *
 * This is the field that actually gets used, so it is not behind an edit button
 * and it has no Save. The rules it has to keep are in `notesAutosave.ts`, which
 * is where they are tested; this file is the timing and the markup.
 */

const DEBOUNCE_MS = 800;

export function VenueNotes({
  wedId,
  venue,
  canWrite = true,
}: {
  wedId: string;
  venue: Venue;
  /** A field that saves itself has to stop saving, not just stop showing Save. */
  canWrite?: boolean;
}) {
  const { t } = useTranslation();
  const patch = usePatchVenueField(wedId);

  const [state, setState] = useState<AutosaveState>(() =>
    initialAutosave(venue.notes ?? '', venue.updatedAt),
  );
  const timer = useRef<ReturnType<typeof setTimeout>>();
  // Read inside callbacks that must not be re-created on every keystroke.
  const latest = useRef(state);
  latest.current = state;
  // `useMutation` hands back a new object on every render, so anything closing
  // over it is a new function on every render too. Held in a ref so the effects
  // below can call the current one without depending on its identity.
  const patchRef = useRef(patch);
  patchRef.current = patch;

  const save = useCallback(() => {
    // `readOnly` already stops the text changing, so `shouldSave` would be false
    // anyway — but a field whose whole job is saving without being asked should
    // say no here too, not rely on a second property to keep it quiet.
    if (!canWrite) return;
    const current = latest.current;
    if (!shouldSave(current)) return;

    const sending = current.text;
    setState(onSaveStart(current));

    patchRef.current.mutate(
      { venueId: venue.venueId, input: { notes: sending, updatedAt: current.baseUpdatedAt } },
      {
        onSuccess: (saved) => {
          setState((s) => onSaved(s, sending, saved.updatedAt));
        },
        onError: (err) => {
          if (err instanceof ApiRequestError && err.code === 'version_conflict') {
            // The row moved. Ask what is there now before deciding anything —
            // the reducer distinguishes "they changed the tour date" from
            // "they wrote in these notes too".
            void fetchVenue(wedId, venue.venueId)
              .then((fresh) => {
                setState((s) => onConflict(s, fresh.notes ?? '', fresh.updatedAt));
              })
              .catch(() => {
                setState((s) => onError(s));
              });
            return;
          }
          setState((s) => onError(s));
        },
      },
    );
  }, [venue.venueId, wedId, canWrite]);

  // The same, so the effects below never list a function as a dependency.
  const saveRef = useRef(save);
  saveRef.current = save;

  // Debounce, so a sentence is one request rather than forty. Keyed on the text
  // and on whether there is anything to send — never on the state object, which
  // is new on every keystroke, nor on `save`, which used to be new on every
  // render and turned this into "save on each render" instead.
  const pending = shouldSave(state);
  useEffect(() => {
    if (!pending) return;
    const id = setTimeout(() => saveRef.current(), DEBOUNCE_MS);
    timer.current = id;
    return () => {
      clearTimeout(id);
    };
  }, [pending, state.text]);

  // A debounce alone loses the last sentence when the drawer closes or the tab
  // goes away, which is the one failure that makes autosave untrustworthy.
  //
  // Mount and unmount only. With `save` in the dependency list this cleanup ran
  // on every render, which fired a save per render, which caused a render — the
  // loop that took the page down.
  useEffect(() => {
    const flush = () => {
      clearTimeout(timer.current);
      saveRef.current();
    };
    const onHide = () => {
      if (document.visibilityState === 'hidden') flush();
    };
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', flush);
    return () => {
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', flush);
      flush();
    };
  }, []);

  // The venue changed under us — the partner saved, or a refetch landed. Adopted
  // only when there is nothing unsaved here.
  useEffect(() => {
    setState((s) => onRemote(s, venue.notes ?? '', venue.updatedAt));
  }, [venue.notes, venue.updatedAt]);

  const status = state.status;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between gap-2">
        <span className="eyebrow">{t('venues.notes')}</span>
        <span className="text-caption text-leaf-soft" role="status" aria-live="polite">
          {status.kind === 'saving' && t('venues.saving')}
          {status.kind === 'saved' && t('venues.saved')}
          {status.kind === 'error' && t('venues.notSaved')}
        </span>
      </div>

      {status.kind === 'conflict' && (
        <div className="panel flex flex-col gap-2 p-3" role="alert">
          <p className="text-body">{t('venues.notesConflict')}</p>
          <p className="whitespace-pre-wrap rounded bg-cream p-2 text-caption">{status.theirs}</p>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                setState((s) => keepMine(s));
              }}
            >
              {t('venues.keepMine')}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setState((s) => takeTheirs(s));
              }}
            >
              {t('venues.keepTheirs')}
            </Button>
          </div>
        </div>
      )}

      <textarea
        readOnly={!canWrite}
        aria-label={t('venues.notes')}
        rows={6}
        value={state.text}
        placeholder={t('venues.notesPlaceholder')}
        onChange={(e) => {
          setState((s) => onType(s, e.target.value));
        }}
        onBlur={() => {
          clearTimeout(timer.current);
          save();
        }}
        className="min-h-[9rem] w-full rounded border border-line bg-paper px-3 py-2 text-body leading-relaxed"
      />
    </div>
  );
}

/** One venue, fetched directly — only used to see what a conflict collided with. */
async function fetchVenue(wedId: string, venueId: string): Promise<Venue> {
  const res = await apiGet<{ venue: Venue }>(`/weddings/${wedId}/venues/${venueId}`);
  return res.venue;
}
